import React, { useCallback, useEffect, useRef } from 'react';
import { ACTIONTYPE } from '../AppStateProvider';
import axios from 'axios';
import { PreflightTest, runPreflight } from 'twilio-video';
import { GetRecaptchaToken } from '../../../RecaptchaProvider';

export default function usePreflightTest(dispatch: React.Dispatch<ACTIONTYPE>, getRecaptchaToken?: GetRecaptchaToken) {
  const preflightTestRef = useRef<PreflightTest>();

  const clearPreflightTest = useCallback(() => {
    preflightTestRef.current = undefined;
  }, []);

  const startPreflightTest = useCallback(() => {
    // Don't start a new Preflight test if one is already running.
    if (preflightTestRef.current) {
      console.warn('Twilio Preflight is already running.');
      return;
    }

    dispatch({ type: 'preflight-started' });

    const tokenPromise = getRecaptchaToken
      ? getRecaptchaToken('preflight').then((recaptchaToken) =>
          axios('app/token', {
            headers: recaptchaToken ? { 'X-Recaptcha-Token': recaptchaToken } : {},
          })
        )
      : axios('app/token');

    return tokenPromise
      .then((response) => {
        console.group('Twilio Preflight');
        console.info('Starting Twilio Preflight test.');
        console.info('Twilio SDK region configuration: default');
        console.groupEnd();

        const preflightTest = runPreflight(response.data.token);

        preflightTestRef.current = preflightTest;

        preflightTest.on('progress', (progress) => {
          console.debug('Twilio Preflight progress:', progress);

          dispatch({
            type: 'preflight-progress',
            progress,
          });
        });

        preflightTest.on('completed', (report) => {
          console.group('Twilio Preflight completed');
          console.info('Full report:', report);

          // Log commonly useful report properties individually so they are
          // easier to inspect in browser developer tools.
          console.info('Test timing:', report.testTiming);
          console.info('Network timing:', report.networkTiming);
          console.info('Stats:', report.stats);

          console.groupEnd();

          clearPreflightTest();

          dispatch({
            type: 'preflight-completed',
            report,
          });

          dispatch({
            type: 'preflight-finished',
          });
        });

        preflightTest.on('failed', (error) => {
          console.group('Twilio Preflight failed');
          console.error('Full error:', error);
          console.error('Error name:', error?.name);
          console.error('Error message:', error?.message);
          console.error('Error stack:', error?.stack);
          console.groupEnd();

          clearPreflightTest();

          dispatch({
            type: 'preflight-failed',
            error,
          });

          dispatch({
            type: 'preflight-finished',
          });
        });
      })
      .catch((error) => {
        console.group('Unable to start Twilio Preflight');
        console.error('Full error:', error);
        console.error('Error name:', error?.name);
        console.error('Error code:', error?.code);
        console.error('Error message:', error?.message);
        console.error('HTTP status:', error?.response?.status);
        console.error('HTTP response:', error?.response?.data);
        console.groupEnd();

        clearPreflightTest();

        dispatch({
          type: 'preflight-token-failed',
          error,
        });

        dispatch({
          type: 'preflight-finished',
        });
      });
  }, [clearPreflightTest, dispatch, getRecaptchaToken]);

  useEffect(() => {
    return () => {
      const preflightTest = preflightTestRef.current;

      if (!preflightTest) {
        return;
      }

      console.info('Stopping Twilio Preflight because the component is unmounting.');

      preflightTest.stop();
      clearPreflightTest();
    };
  }, [clearPreflightTest]);

  return { startPreflightTest } as const;
}
